import React, { useState, useEffect } from 'react';
import productService from '../services/productService';
import './ProductsTab.css';

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    Product_Name: '',
    qty: 1,
    unit: 'Uses',
    price: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Available units
  const units = ['Uses', 'ml', 'g', 'oz', 'L'];

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Filter products when search term changes
  useEffect(() => {
    if (products.length > 0) {
      const filtered = products.filter(product => 
        product.Product_Name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
      setCurrentPage(1); // Reset to first page when filtering
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAllProducts();
      const productsData = response.data || [];
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      Product_Name: '',
      qty: 1,
      unit: 'Uses',
      price: ''
    });
    setEditMode(false);
    setEditId(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    });
  };
  
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  const productData = {
    Product_Name: formData.Product_Name,
    Container_Size: formData.qty,              // ← Fixed
    Unit: formData.unit,                       // ← Fixed
    Total_Container_Cost: parseFloat(formData.price)  // ← Fixed
  };
  
  try {
    if (editMode) {
      await productService.updateProduct(editId, productData);
      alert("Product updated successfully");
    } else {
      await productService.createProduct(productData);
      alert("Product added successfully");
    }
    
    resetForm();
    fetchProducts();
    setShowForm(false);
  } catch (error) {
    console.error('Error saving product:', error);
    alert("Error saving product. Please try again.");
  }
};
  
  const handleEdit = (product) => {
    setFormData({
      Product_Name: product.Product_Name,
      qty: product.qty || 1,
      unit: product.unit || 'Uses',
      price: product.price
    });
    setEditMode(true);
    setEditId(product.Product_ID);
    setShowForm(true);
  };
  
  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.deleteProduct(productId);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  return (
    <div className="products-container">
      <h2>Product Library</h2>
      
      <div className="product-instructions">
        <p>Maintain your product inventory here with accurate costs to ensure precise service pricing.</p>
        <p>When you add products to services later, the system will automatically calculate the exact cost per application.</p>
        <p>Update product prices here anytime, and all your service costs will be recalculated instantly!</p>
      </div>
      
      <div className="products-actions-bar">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="view-controls">
          <button 
            className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            Cards
          </button>
          <button 
            className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Table
          </button>
        </div>
        
        <button 
          className="btn-toggle-form" 
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) resetForm();
          }}
        >
          {showForm ? 'Hide Form' : 'Add New Product'}
        </button>
      </div>
      
      {showForm && (
        <div className="product-form">
          <h3>{editMode ? 'Edit Product' : 'Add New Product'}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Product Name</label>
              <input
                type="text"
                placeholder="Enter product name"
                value={formData.Product_Name}
                name="Product_Name"
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Container Size</label>
              <input
                type="number"
                name="qty"
                value={formData.qty}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Unit</label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
              >
                {units.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Total Container Cost (£)</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-save" 
              onClick={handleSubmit}
            >
              {editMode ? 'Update Product' : 'Save Product'}
            </button>
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="loading">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="empty-products">
          {searchTerm ? (
            <p>No products found matching "{searchTerm}". Try a different search term or add a new product.</p>
          ) : (
            <p>No products found. Add your first product using the button above.</p>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        // Card View
        <>
          <div className="product-cards">
            {currentProducts.map(product => (
              <div key={product.Product_ID} className="product-card">
                <div className="product-card-header">
                  <h4>{product.Product_Name}</h4>
                </div>
                <div className="product-card-body">
                  <div className="product-details">
                    <div className="detail-item">
                      <span className="detail-label">Container Size:</span>
                      <span className="detail-value">{product.qty} {product.unit || 'Uses'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Cost per Container:</span>
                      <span className="detail-value">£{parseFloat(product.price).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Cost per Unit:</span>
                      <span className="detail-value">
                        £{(parseFloat(product.price) / product.qty).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="product-card-footer">
                  <button 
                    className="btn-edit" 
                    onClick={() => handleEdit(product)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-delete" 
                    onClick={() => handleDelete(product.Product_ID)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination for Card View */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
                className="pagination-button"
              >
                First
              </button>
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                className="pagination-button"
              >
                &laquo; Prev
              </button>
              
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next &raquo;
              </button>
              <button 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Last
              </button>
            </div>
          )}
        </>
      ) : (
        // Table View
        <>
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Container Size</th>
                  <th>Unit</th>
                  <th>Container Cost</th>
                  <th>Cost per Unit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map(product => (
                  <tr key={product.Product_ID}>
                    <td>{product.Product_Name}</td>
                    <td>{product.qty}</td>
                    <td>{product.unit || 'Uses'}</td>
                    <td>£{parseFloat(product.price).toFixed(2)}</td>
                    <td>£{(parseFloat(product.price) / product.qty).toFixed(2)}</td>
                    <td>
                      <div className="table-actions">
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEdit(product)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-delete" 
                          onClick={() => handleDelete(product.Product_ID)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination for Table View */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
                className="pagination-button"
              >
                First
              </button>
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                className="pagination-button"
              >
                &laquo; Prev
              </button>
              
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next &raquo;
              </button>
              <button 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Last
              </button>
            </div>
          )}
        </>
      )}
      
      <div className="products-count">
        Showing {currentProducts.length} of {filteredProducts.length} products
        {searchTerm && ` (filtered from ${products.length} total)`}
      </div>
    </div>
  );
};

export default ProductsTab;